/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply. */
import * as React from 'react';

/** The minimal row surface this hook needs — satisfied by both DimeNestedTable's ParentRow
 *  (flat, level-1 only) and DimeTreeTable's tree Row (arbitrary depth via getSubRows). */
interface KeyboardNavigableRow {
  id: string;
}

interface RowKeyboardNavigationHelpers {
  /** Moves the roving-tabindex focus to another row by id, e.g. a parent or first child. */
  moveActiveRow: (rowId: string) => void;
}

interface RowKeyboardNavigationOptions<TRow extends KeyboardNavigableRow> {
  rows: TRow[];
  /**
   * Handles every key this hook does not already own (ArrowUp/ArrowDown move the roving
   * tabindex to the previous/next row in `rows`, which is identical for a flat parent list and a
   * flattened, depth-aware tree list). Expand/collapse, first-child, parent-navigation, and
   * Enter/Space all differ by consumer, so they're injected here instead of hardcoded.
   */
  onKeyDown: (event: React.KeyboardEvent, row: TRow, helpers: RowKeyboardNavigationHelpers) => void;
}

interface RowKeyboardNavigation<TRow extends KeyboardNavigableRow> {
  resolvedActiveRowId: string | undefined;
  registerRowElement: (rowId: string, element: HTMLTableRowElement | null) => void;
  setActiveRowId: (rowId: string) => void;
  handleRowBlur: () => void;
  handleRowKeyDown: (event: React.KeyboardEvent, row: TRow) => void;
}

/** Roving-tabindex navigation over table rows, keyed by row id so it survives a re-sort. */
export function useRowKeyboardNavigation<TRow extends KeyboardNavigableRow>({
  rows,
  onKeyDown,
}: RowKeyboardNavigationOptions<TRow>): RowKeyboardNavigation<TRow> {
  const [activeRowId, setActiveRowId] = React.useState<string | undefined>(undefined);
  const rowElementsById = React.useRef(new Map<string, HTMLTableRowElement>());
  // True while a row itself holds focus, as opposed to something inside its child panel/content.
  const rowHasFocus = React.useRef(false);

  const registerRowElement = React.useCallback(
    (rowId: string, element: HTMLTableRowElement | null) => {
      if (element) rowElementsById.current.set(rowId, element);
      else rowElementsById.current.delete(rowId);
    },
    [],
  );

  // DOM focus must follow the active row, or the next arrow key goes to the row still holding
  // focus and movement stalls after one keystroke.
  const moveActiveRow = React.useCallback((nextRowId: string) => {
    setActiveRowId(nextRowId);
    rowElementsById.current.get(nextRowId)?.focus();
  }, []);

  function handleRowKeyDown(event: React.KeyboardEvent, row: TRow) {
    const index = rows.findIndex((candidate) => candidate.id === row.id);
    if (event.key === 'ArrowDown' && index < rows.length - 1) {
      event.preventDefault();
      moveActiveRow(rows[index + 1]!.id);
    } else if (event.key === 'ArrowUp' && index > 0) {
      event.preventDefault();
      moveActiveRow(rows[index - 1]!.id);
    } else {
      onKeyDown(event, row, { moveActiveRow });
    }
  }

  // A page/data change replaces the row set, so a stale activeRowId would leave every row at
  // tabIndex -1 and drop the table out of the tab order.
  const activeRowIsPresent =
    activeRowId !== undefined && rows.some((candidate) => candidate.id === activeRowId);

  // Restoring tabIndex is not enough: when the active row unmounts, the browser drops focus to
  // <body> and the next arrow key does nothing until the user tabs back in. Only reclaim focus if
  // it actually fell out of the document — if the user has moved to a pagination button or
  // anything else, stealing it back would be worse than the stall.
  const wasActiveRowPresent = React.useRef(activeRowIsPresent);
  React.useEffect(() => {
    const focusWasLost =
      document.activeElement === null || document.activeElement === document.body;
    // Reclaim only when a ROW held focus. A child panel/collapsible content that unmounts in the
    // same commit also drops focus to <body> — pulling it to row 1 there would throw the user out
    // of the content they were working in. rowHasFocus is maintained by the rows' own
    // onFocus/onBlur, which is the only signal still accurate here: by the time an effect runs,
    // document.activeElement is already <body>.
    if (
      wasActiveRowPresent.current &&
      !activeRowIsPresent &&
      focusWasLost &&
      rowHasFocus.current &&
      rows.length > 0
    ) {
      rowElementsById.current.get(rows[0]!.id)?.focus();
    }
    wasActiveRowPresent.current = activeRowIsPresent;
  }, [activeRowIsPresent, rows]);

  const handleRowFocus = React.useCallback((rowId: string) => {
    rowHasFocus.current = true;
    setActiveRowId(rowId);
  }, []);

  const handleRowBlur = React.useCallback(() => {
    rowHasFocus.current = false;
  }, []);

  return {
    resolvedActiveRowId: activeRowIsPresent ? activeRowId : rows[0]?.id,
    registerRowElement,
    setActiveRowId: handleRowFocus,
    handleRowBlur,
    handleRowKeyDown,
  };
}
